import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getRequestHandler, requestHandler } from '@/services/requestHandler';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { OnboardingAdminRequests, OnboardingPersonalRequests } from '@/services/requests/onboarding/OnboardingRequests';
import { UsersRequests } from '@/services/requests/users/UsersRequests';
import {
  BusinessActorDocument, CorporateDocumentType, DocumentSide, LoadedDocument, OnboardingDocument, ReviewedDocument, UnionDocument,
} from '@/types/interfaces/Document.interface';
import { filterNullFromObject } from '@/utils/filterNullFromObject';
import { formatOnboardingUserData } from '@/utils/formatOnboardingUserData';
import { getCleanedPayload } from '@/utils/getCleanedPayload';
import { hasValues } from '@/utils/hasValues';

export const usePersonalOnboardingStore = defineStore('PersonalOnboarding', () => {
  const isApplicationLoaded = ref(false);
  const id = ref('');
  const email = ref('');
  const phone = ref('');
  const completedStages = ref<Onboarding.Personal.StagesPayload[]>([]);
  const applicationType = ref<Onboarding.ApplicationType>('PERSONAL');
  const partnerAttributes = ref<Onboarding.PartnerAttributes>();
  const personalDetails = ref<Onboarding.Personal.PersonalDetails>({
    dateOfBirth: '',
    firstName: '',
    lastName: '',
    nationalityIso3: '',
    mailingAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      countryIso3: '',
      postalCode: '',
    },
    occupation: '',
  });
  const userDetails = ref<Onboarding.UserDetails>({
    userId: '',
    organizationId: '',
    profileOrganizationId: '',
    role: undefined,
    permissions: [],
    email: '',
    phone: '',
  });
  const personalDocs = ref<Record<BusinessActorDocument, LoadedDocument> | null>(null);
  const reviewedDocuments = ref<ReviewedDocument[]>();
  const applicationStatus = ref<Onboarding.ApplicationStatus>();
  const contract = ref<{ id: string } | undefined>({ id: '' });
  const personalDocuments = ref<Record<UnionDocument, OnboardingDocument>>();
  const riskLevel = ref<Onboarding.RiskLevel>();
  const createAt = ref('');
  const blocked = ref(false);
  const banned = ref<boolean>();
  const isPapiWithoutUserDetails = ref(false);

  function setData(data: Onboarding.Personal.Application) {
    const {
      id: dataId,
      email: dataEmail,
      phone: dataPhone,
      completedStages: dataCompletedStages,
      personalDocs: dataPersonalDocs,
      personalDetails: dataPersonalDetails,
      applicationStatus: dataApplicationStatus,
      partnerAttributes: dataPartnerAttributes,
      userDetails: dataUserDetails,
      contract: dataContract,
      personalDocuments: dataPersonalDocuments,
      createAt: dataCreateAt,
      riskLevel: dataRiskLevel,
      blocked: dataBlocked,
      reviewedDocuments: dataReviewedDocuments,
    } = formatOnboardingUserData(data);

    id.value = dataId;
    email.value = dataEmail || '';
    phone.value = dataPhone || '';
    completedStages.value = dataCompletedStages || [];
    applicationStatus.value = dataApplicationStatus;
    partnerAttributes.value = dataPartnerAttributes;
    personalDetails.value = filterNullFromObject(dataPersonalDetails) as Onboarding.Personal.PersonalDetails;
    personalDocs.value = dataPersonalDocs;
    userDetails.value = dataUserDetails || {
      userId: '',
      organizationId: '',
      profileOrganizationId: '',
      role: undefined,
      permissions: [],
      email: '',
      phone: '',
    };
    contract.value = dataContract || { id: '' };
    personalDocuments.value = dataPersonalDocuments;
    createAt.value = dataCreateAt || '';
    riskLevel.value = dataRiskLevel;
    blocked.value = dataBlocked || false;
    isPapiWithoutUserDetails.value = dataPartnerAttributes?.partnerId !== undefined && !dataUserDetails;
    reviewedDocuments.value = dataReviewedDocuments;
  }

  const fetchBannedStatus = getRequestHandler(UsersRequests.getUser, {
    onSuccess: (res) => {
      banned.value = res.banned;
    },
  });

  async function fetchAdminApplication(applicationId: string, force?: boolean) {
    if (!force && isApplicationLoaded.value) return null;

    return requestHandler(() => OnboardingAdminRequests.Personal.getApplication(applicationId), {
      pending: (newValue) => {
        isApplicationLoaded.value = !newValue;
      },
      onSuccess: (res) => {
        if (res.userDetails?.userId) fetchBannedStatus(res.userDetails.userId);
        setData(res);
      },
    });
  }

  async function fetchIndividualApplication(force?: boolean) {
    if (!force && isApplicationLoaded.value) return null;

    const response = await requestHandler(() => OnboardingPersonalRequests.getApplication(), {
      pending: (newValue) => {
        isApplicationLoaded.value = !newValue;
      },
      onSuccess: setData,
    });

    return response.response;
  }

  const personalDetailsReq = {
    saveUser: getRequestHandler(OnboardingPersonalRequests.updatePersonalDetails, {
      onSuccess: setData,
    }),
    complete: getRequestHandler(OnboardingPersonalRequests.completePersonalDetails, {
      onSuccess: setData,
    }),
  };

  const personalDocumentsReq = {
    upload: async (data: Onboarding.Personal.Payload.UploadDocument) => {
      const response = await OnboardingPersonalRequests.uploadDocument(data);
      if (response.error === null) setData(response.response);
      return response;
    },
    read: async (documentType: BusinessActorDocument, documentSide: DocumentSide) => (
      OnboardingPersonalRequests.getDocument(documentType, documentSide)
    ),
  };

  const personalDetailsAdminReq = {
    updateUser: async (applicationId: string | string[], data: Onboarding.Personal.PersonalDetails) => {
      const response = await OnboardingAdminRequests.Personal.updatePersonalDetails(applicationId, getCleanedPayload(data));
      if (response.error === null) setData(response.response);
      return response;
    },
    updateContract: async (actorId: string, contractId: string) => {
      const response = await OnboardingAdminRequests.Personal.updateContract(actorId, contractId);
      if (response.error === null) setData(response.response);
      return response;
    },
    uploadDocument: async (applicationId: string | string[], fileData: {
      type: CorporateDocumentType;
      formData: FormData;
      issuingCountryIso3?: string;
      side?: DocumentSide;
    }) => {
      const response = await OnboardingAdminRequests.Personal.uploadPersonalDocument(applicationId, fileData);
      if (response.error === null) setData(response.response);
      return response;
    },
  };

  async function toggleBlockStatus() {
    const request = blocked.value
      ? OnboardingAdminRequests.Shared.unblockApplication
      : OnboardingAdminRequests.Shared.blockApplication;

    return requestHandler(() => request(id.value), {
      onSuccess: (res) => {
        blocked.value = res.blocked;
      },
    });
  }

  async function unban() {
    const userIdValue = userDetails.value?.userId;
    if (userIdValue === undefined) throw new Error('User ID is undefined');
    return requestHandler(() => UsersRequests.unban(userIdValue), {
      successNotification: true,
      onSuccess: () => {
        banned.value = false;
      },
    });
  }

  const close = getRequestHandler(() => OnboardingAdminRequests.Shared.closeAccount(id.value), {
    onSuccess: (res) => {
      applicationStatus.value = res.applicationStatus;
    },
    errorNotification: false,
  });

  const updateRiskLevelAction = getRequestHandler(
    (riskLevelValue: Onboarding.RiskLevel) => OnboardingAdminRequests.Personal.updateRiskLevel(id.value, riskLevelValue),
    { onSuccess: setData },
  );

  const updateUserContacts = getRequestHandler(
    (newContact: Record<string, string | undefined>) => OnboardingAdminRequests.Shared.updateUserContact(id.value, newContact),
  );

  const personalOnboardingStoreActions = {
    getLivenessCheckIFrame: getRequestHandler(OnboardingPersonalRequests.getLivenessCheckIFrame),
    acceptPricing: getRequestHandler(
      () => OnboardingPersonalRequests.acceptContractPricing(),
      { onSuccess: setData },
    ),
  };

  const personalDetailsStages = computed(() => ({
    info: !!(personalDetails.value?.firstName
      && personalDetails.value?.lastName
      && personalDetails.value?.dateOfBirth
      && personalDetails.value?.nationalityIso3
      && personalDetails.value?.occupation
    ),
    address: hasValues(personalDetails.value?.mailingAddress, 'addressLine2'),
    turnover: personalDetails.value?.annualTurnover,
    fundsSource: personalDetails.value?.fundsSource,
  }));

  function $reset() {
    isApplicationLoaded.value = false;
    id.value = '';
    email.value = '';
    phone.value = '';
    completedStages.value = [];
    applicationType.value = 'PERSONAL';
    partnerAttributes.value = undefined;
    personalDetails.value = {
      dateOfBirth: '',
      firstName: '',
      lastName: '',
      nationalityIso3: '',
      mailingAddress: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        countryIso3: '',
        postalCode: '',
      },
      occupation: '',
    };
    userDetails.value = {
      userId: '',
      organizationId: '',
      profileOrganizationId: '',
      role: undefined,
      permissions: [],
      email: '',
      phone: '',
    };
    personalDocs.value = null;
    reviewedDocuments.value = undefined;
    applicationStatus.value = undefined;
    contract.value = { id: '' };
    personalDocuments.value = undefined;
    riskLevel.value = undefined;
    createAt.value = '';
    blocked.value = false;
    banned.value = undefined;
    isPapiWithoutUserDetails.value = false;
  }

  function getPersonalDetailsStages() {
    return {
      info: !!(personalDetails.value?.firstName
        && personalDetails.value?.lastName
        && personalDetails.value?.dateOfBirth
        && personalDetails.value?.nationalityIso3
        && personalDetails.value?.occupation
      ),
      address: hasValues(personalDetails.value?.mailingAddress, 'addressLine2'),
      turnover: personalDetails.value?.annualTurnover,
      fundsSource: personalDetails.value?.fundsSource,
    };
  }

  return {
    isApplicationLoaded,
    id,
    email,
    phone,
    completedStages,
    applicationType,
    partnerAttributes,
    personalDetails,
    userDetails,
    personalDocs,
    reviewedDocuments,
    applicationStatus,
    contract,
    personalDocuments,
    riskLevel,
    createAt,
    blocked,
    banned,
    isPapiWithoutUserDetails,

    personalDetailsStages,

    fetchAdminApplication,
    fetchIndividualApplication,
    toggleBlockStatus,
    unban,
    close,
    updateRiskLevel: updateRiskLevelAction,
    updateUserContacts,
    $reset,

    getPersonalDetailsStages,

    personalDetailsReq,
    personalDocumentsReq,
    personalDetailsAdminReq,
    personalOnboardingStoreActions,
  };
});
